import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NewsCard, { type NewsItem } from "@/components/NewsCard";

const PAGE_SIZE = 20;

const News = () => {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [sources, setSources] = useState<{ id: string; name: string }[]>([]);
  const [filter, setFilter] = useState<string>("All");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    supabase
      .from("news_sources")
      .select("id, name")
      .eq("published", true)
      .order("sort_order")
      .then(({ data }) => setSources(data ?? []));
  }, []);

  useEffect(() => {
    setItems([]);
    setPage(0);
    setHasMore(true);
    loadPage(0, filter, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadPage = async (p: number, f: string, reset = false) => {
    setLoading(true);
    let q = supabase
      .from("news_items")
      .select("id, title, url, summary, image_url, published_at, fetched_at, news_sources(name, url, logo_path)")
      // Source publication date drives the order; undated items fall to the end.
      .order("published_at", { ascending: false, nullsFirst: false })
      .order("fetched_at", { ascending: false })
      .range(p * PAGE_SIZE, p * PAGE_SIZE + PAGE_SIZE - 1);
    if (f !== "All") q = q.eq("source_id", f);

    const { data } = await q;
    const mapped: NewsItem[] = (data ?? []).map((r: any) => ({
      id: r.id,
      title: r.title,
      url: r.url,
      summary: r.summary,
      image_url: r.image_url,
      published_at: r.published_at,
      fetched_at: r.fetched_at,
      source: r.news_sources
        ? { name: r.news_sources.name, url: r.news_sources.url, logo_path: r.news_sources.logo_path }
        : null,
    }));
    setItems((prev) => (reset ? mapped : [...prev, ...mapped]));
    setHasMore(mapped.length === PAGE_SIZE);
    setLoading(false);
  };


  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    loadPage(next, filter);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="py-16 md:py-24">
          <div className="container">
            <div className="max-w-2xl">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                The feed
              </span>
              <h1 className="text-5xl md:text-7xl mt-3 text-balance">Women's sports news.</h1>
              <p className="text-muted-foreground mt-3">
                Aggregated coverage from the publications championing women in sport.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mt-8">
              {[{ id: "All", name: "All" }, ...sources].map((s) => (
                <button
                  key={s.id}
                  onClick={() => setFilter(s.id)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    filter === s.id
                      ? "bg-secondary text-secondary-foreground border-secondary"
                      : "bg-background text-foreground border-border hover:border-secondary"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
              {items.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>

            {!loading && items.length === 0 && (
              <p className="text-center text-muted-foreground py-16">
                No articles yet. Check back shortly.
              </p>
            )}


            {hasMore && items.length > 0 && (
              <div className="text-center mt-10">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-6 py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
                >
                  {loading ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default News;
